/* cae-link-clicks.js — every internal link that nothing else records.
 *
 * WHY THIS FILE EXISTS (measured on ROW, 7 days to 2026-09-09, BigQuery
 * events_dedup): one page view in five that follows another page view has no
 * click anywhere before it. Collection -> product is nearly always explained
 * (card_click); product -> product is unexplained 30% of the time, product ->
 * collection 20%, product -> home 61%, and the story a person reads is "the
 * shopper arrived on a page without the click that took them there". The
 * emitters cover their surfaces well - card_click the product cards, nav_click
 * the menus and the footer, home_element_click the home slides and collection
 * cards - and nothing covers the rest: the logo, the cart / search / account
 * icons, pagination, collection cards on any page but the home, the horizontal
 * cards (cart cross-sell, complementary products, search results), links inside
 * the cart drawer, filters, and plain links in the page's own copy.
 *
 * So: ONE capture-phase listener, one event name, `link_click`, and a `kind`
 * that names the surface. It steps aside for every surface another emitter
 * already claims (same tests they use, so a click is counted once), and it
 * never blocks or delays the click. Every field rides in `detail` (Tier A: no
 * column, no ingest deploy). Query with JSON_VALUE(detail, '$.kind').
 *
 * WHEN BIG-WORK IS PROMOTED: its cae-extra-events.js reports the logo and the
 * header icons under nav_click (depth 'logo' / 'icon'). At that moment the two
 * `kind`s 'logo' and 'header_icon' below are counted twice - delete the header
 * branch in classify() and this file goes on covering everything else.
 *
 * The other half of the same finding lives in cae-client.js: page_view now
 * says HOW the page was reached (nav_type: navigate / reload / back_forward /
 * bfcache), and a page restored from the back-forward cache emits a page_view
 * again - a third of mobile card clicks used to land on a page the story had
 * never shown the shopper returning to.
 *
 * Loaded defer after cae-client.js in layout/theme.liquid; emits through
 * window.CAEDev (queued until it exists, like cae-extra-events.js).
 * Kill switch: window.CAE_DISABLED === true (cae-client's) or
 * window.CAE_LINK_CLICKS_OFF === true (this file alone).
 */
(function () {
    'use strict';

    if (window.CAE_DISABLED === true || window.CAE_LINK_CLICKS_OFF === true) return;

    /* ---- emit bridge: queue until CAEDev (cae-client.js) is ready ---------- */
    var _pending = [];
    var _ready = false;
    function emit(name, detail) {
        try { if (window.AGB_consoleCat && window.AGB_consoleCat('interaction')) console.log('[cae-extra]', name, detail || {}); } catch (e) {}
        try {
            if (_ready && window.CAEDev && typeof window.CAEDev.emit === 'function') {
                return window.CAEDev.emit(name, detail || {});
            }
            _pending.push([name, detail || {}]);
        } catch (e) {}
        return null;
    }
    function _drain() {
        try {
            if (!(window.CAEDev && typeof window.CAEDev.emit === 'function')) return false;
            _ready = true;
            for (var i = 0; i < _pending.length; i++) {
                try { window.CAEDev.emit(_pending[i][0], _pending[i][1]); } catch (e) {}
            }
            _pending.length = 0;
            return true;
        } catch (e) { return false; }
    }
    function _waitForCae(attempt) {
        if (_drain()) return;
        attempt = attempt || 0;
        if (attempt > 50) return;
        setTimeout(function () { _waitForCae(attempt + 1); }, 100);
    }

    /* ---- small DOM helpers (same shapes as cae-extra-events.js) ------------ */
    function text(el) {
        try {
            var t = (el && (el.textContent || '')).replace(/\s+/g, ' ').trim();
            return t ? t.slice(0, 120) : null;
        } catch (e) { return null; }
    }
    function attr(el, name) { try { return el && el.getAttribute ? el.getAttribute(name) : null; } catch (e) { return null; } }
    function isHome() {
        try { var p = location.pathname || ''; return p === '/' || p === ''; } catch (e) { return false; }
    }
    function pageType() {
        var p = location.pathname || '';
        if (/^\/products\//.test(p))                       return 'product';
        if (/\/collections\/[^\/]+\/products\//.test(p))   return 'product';
        if (/^\/collections\//.test(p))                    return 'collection';
        if (/^\/cart/.test(p))                             return 'cart';
        if (p === '/' || p === '')                         return 'home';
        return 'other';
    }
    function sectionInfo(el) {
        try {
            var sec = el && el.closest ? el.closest('[id^="shopify-section-"]') : null;
            if (!sec) return { id: null, type: null };
            var m = (sec.className || '').match(/shopify-section--([\w-]+)/);
            return { id: sec.id || null, type: m ? m[1] : null };
        } catch (e) { return { id: null, type: null }; }
    }
    // The link as a same-site path, or the full url when it leaves the site.
    function resolve(href) {
        try {
            var u = new URL(href, location.href);
            var external = u.host !== location.host;
            return {
                external: external,
                path: external ? u.href : (u.pathname + (u.search || '')),
                product: (u.pathname.match(/\/products\/([^\/\?#]+)/) || [])[1] || null,
                collection: (u.pathname.match(/\/collections\/([^\/\?#]+)/) || [])[1] || null
            };
        } catch (e) { return { external: false, path: href || null, product: null, collection: null }; }
    }

    /* ---- what the link is - or null when another emitter records it ------- */
    function classify(a) {
        // Product cards: card_click (cae-client.js).
        if (a.closest('product-card, .product-card')) return null;
        // The media feed records its own clicks (agmf_click).
        if (a.closest('[class^="agmf-"], [class*=" agmf-"]')) return null;
        // Header: menus are nav_click; the logo and the icons were nobody's.
        var header = a.closest('x-header');
        if (header) {
            if (a.closest('.header__logo')) return 'logo';
            if (a.closest('.header__secondary-nav')) return 'header_icon';
            return null;
        }
        // Mobile menu drawer + footer: nav_click.
        if (a.closest('#sidebar-menu, header-sidebar, .header-sidebar')) return null;
        if (a.closest('.shopify-section--footer, .footer')) return null;
        // Home slides and home collection cards: home_element_click.
        if (isHome() && (a.matches('.slideshow__slide') || a.matches('.collection-card'))) return null;

        if (a.matches('.collection-card') || a.closest('.collection-card')) return 'collection_card';
        if (a.closest('.pagination')) return 'pagination';
        if (a.closest('[class*="breadcrumb"], nav[aria-label*="readcrumb"]')) return 'breadcrumb';
        if (a.closest('predictive-search, .predictive-search')) return 'search_result';
        if (a.closest('cart-drawer, .cart-drawer, #cart-drawer')) return 'cart_drawer';
        if (a.closest('.horizontal-product-card')) return 'horizontal_card';
        if (a.closest('product-recommendations')) return 'recommendation';
        if (a.closest('.facets, facet-filters, .facet-filters, [is="facet-link"]') || attr(a, 'is') === 'facet-link') return 'facet';
        var to = resolve(attr(a, 'href'));
        if (to.external) return 'external';
        if (to.product) return 'product_link';
        if (to.collection) return 'collection_link';
        return 'link';
    }

    function install() {
        document.addEventListener('click', function (e) {
            try {
                var t = e.target;
                if (!t || t.nodeType !== 1 || !t.closest) return;
                var a = t.closest('a[href]');
                if (!a) return;
                var href = attr(a, 'href') || '';
                // in-page anchors and non-navigations are not arrivals anywhere
                if (!href || /^(#|javascript:|mailto:|tel:)/i.test(href)) return;
                var kind = classify(a);
                if (!kind) return;
                var to = resolve(href);
                var sec = sectionInfo(a);
                emit('link_click', {
                    kind:              kind,
                    href:              to.path,
                    label:             text(a) || attr(a, 'aria-label') || attr(a, 'title'),
                    product_handle:    to.product,
                    collection_handle: to.collection,
                    section_id:        sec.id,
                    section_type:      sec.type,
                    page_type:         pageType(),
                    is_external:       to.external,
                    // A middle click, a ctrl/cmd click or target=_blank opens a new
                    // tab: the next page view lands in another tab, not after this one.
                    new_tab:           !!(e.button === 1 || e.ctrlKey || e.metaKey || attr(a, 'target') === '_blank')
                });
            } catch (err) {}
        }, true);
        // auxclick is the middle button on browsers that do not send it as click
        document.addEventListener('auxclick', function (e) {
            try {
                if (!e || e.button !== 1) return;
                var t = e.target;
                if (!t || t.nodeType !== 1 || !t.closest) return;
                var a = t.closest('a[href]');
                if (!a) return;
                var kind = classify(a);
                if (!kind) return;
                var to = resolve(attr(a, 'href') || '');
                var sec = sectionInfo(a);
                emit('link_click', {
                    kind: kind, href: to.path, label: text(a) || attr(a, 'aria-label'),
                    product_handle: to.product, collection_handle: to.collection,
                    section_id: sec.id, section_type: sec.type, page_type: pageType(),
                    is_external: to.external, new_tab: true
                });
            } catch (err) {}
        }, true);
    }

    _waitForCae(0);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', install);
    else install();
})();
